'use client';
import { useEffect, useState, type RefObject } from 'react';
import { Portal, Select, createListCollection, SelectValueChangeDetails } from '@chakra-ui/react';
import { useMap } from '@/components/map/map-context';
import { storageManager } from '@/lib/utils/storage-manager';
import { MAP_ZOOM_LEVEL, MAP_ZOOM_LEVEL_MOBILE } from '@/lib/constants';

// Utility function to detect mobile screens
const isMobileScreen = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768; // Common mobile breakpoint
};

const zoomPresets = createListCollection({
  items: [
    { label: 'Zoom: 5 (1 : 17,699,856)', value: '5' },
    { label: 'Zoom: 6 (1 : 8,849,928)', value: '6' },
    { label: 'Zoom: 7 (1 : 4,482,106)', value: '7' },
    { label: 'Zoom: 8 (1 : 2,298,196)', value: '8' },
    { label: 'Zoom: 9 (1 : 1,206,240)', value: '9' },
    { label: 'Zoom: 10 (1 : 660,263)', value: '10' },
    { label: 'Zoom: 11 (1 : 387,274)', value: '11' },
    { label: 'Zoom: 12 (1 : 250,779)', value: '12' },
    { label: 'Zoom: 13 (1 : 150,000)', value: '13' },
    { label: 'Zoom: 14 (1 : 125,000)', value: '14' },
    { label: 'Zoom: 15 (1 : 100,000)', value: '15' },
    { label: 'Zoom: 16 (1 : 75,000)', value: '16' },
    { label: 'Zoom: 17 (1 : 50,000)', value: '17' },
    { label: 'Zoom: 18 (1 : 25,000)', value: '18' },
    { label: 'Zoom: 19 (1 : 12,500)', value: '19' },
    { label: 'Zoom: 20 (1 : 5,000)', value: '20' },
  ],
});

export const ZoomSelection = ({ portalRef }: { portalRef?: RefObject<HTMLDivElement | null> }) => {
  const { map } = useMap();
  const [selectedZoom, setSelectedZoom] = useState<number>(() => {
    // Load zoom level from storage manager
    const storedZoom = storageManager.loadZoomLevel();
    
    // If no stored value or stored value is the default desktop zoom, check for mobile
    if (storedZoom === MAP_ZOOM_LEVEL && isMobileScreen()) {
      return MAP_ZOOM_LEVEL_MOBILE;
    }
    
    return storedZoom;
  });


  // Keep the select value in sync with the map's current zoom level
  useEffect(() => {
    if (!map) return;
    const view = map.getView();
    const handleZoomChange = () => {
      const newZoom = view.getZoom();
      if (newZoom !== undefined) {
        const roundedZoom = Math.floor(newZoom);
        setSelectedZoom((prev) => (prev !== roundedZoom ? roundedZoom : prev));
      }
    };
    view.on('change:resolution', handleZoomChange);
    // Initialize selected value with current map zoom
    handleZoomChange();
    return () => {
      view.un('change:resolution', handleZoomChange);
    };
  }, [map]);

  const handleChange = (details: SelectValueChangeDetails<{ label: string; value: string }>) => {
    if (details?.value && details.value.length > 0) {
      const newZoom = Number(details.value[0]);
      setSelectedZoom(newZoom);
      storageManager.saveZoomLevel(newZoom);
      if (map) {
        const view = map.getView();
        view.setZoom(newZoom);
      }
    }
  };

  return (
    <Select.Root
      size='sm'
      width='full'
      collection={zoomPresets}
      value={[selectedZoom.toString()]}
      onValueChange={handleChange}
    >
      <Select.HiddenSelect />
      <Select.Label>Zoom (Scale) Preset:</Select.Label>
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText placeholder='Please select a preset' />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal container={portalRef}>
        <Select.Positioner style={{ zIndex: 1500 }}>
          <Select.Content>
            {zoomPresets.items.map((item) => (
              <Select.Item item={item} key={item.value}>
                {item.label}
                <Select.ItemIndicator />
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
};
